import produce from 'immer';
import { AirCalendarData, Price, PriceData, TravelType, WidgetTab } from '../../common';
import {
  cancelAirCalendar,
  cancelGetPricesForFollowingMonth,
  getAirCalendar,
  getPricesForFollowingMonth,
  getPricesForFullYear,
  Response,
  Service,
  Status,
} from '../../services';
import { changed, formatToHistogramData, isEmpty, isNotEmpty, isPresent } from '../../utils';
import { bookingFlowDeepLink } from '../../utils/deeplinkUtils';
import { BookingState, PriceState, SelectionState } from '../state';
import {
  currentDateAvailable,
  currentDateChanged,
  isLoading,
  locationsChanged,
  preDatesAndDepartureSelected,
  preDatesExcludingPaxAmountChanged,
  preDatesOrDepartureChanged,
  preDatesSelected,
  selectionChanged,
} from '../stateUtils';
import { LifecycleEvent, SideEffect, SideEffectGenerator, Store } from '../types';
import { bindActions } from 'redux-zero/utils';

const UNSUPPORTED_OND_STATUS_CODE = '224';

// == Utilities ================================================================

const calculateCurrentLowestPrice = (
  { travelDates: { departureDate, returnDate }, travelType }: SelectionState,
  { fullYear, followingMonth }: PriceState
): Price => {
  let price: Price;
  if (travelType === TravelType.ONE_WAY) {
    price = fullYear?.[departureDate];
  } else {
    price = followingMonth?.[returnDate];
  }
  return isPresent(price) && isPresent(price.amount) ? price : undefined;
};

export const getPricesForFullYearEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getPricesForFullYearStart: (state) =>
        produce(state, ({ ux, price }: BookingState) => {
          ux.loading.getPricesForFullYear = true;
          delete price.fullYear;
          delete price.availability;
          delete price.weeklyFlights;
          delete price.error;
        }),
      _getPricesForFullYearFinish: (state, { status, payload }: Response<PriceData>) =>
        produce(state, ({ selection, ux, price }: BookingState) => {
          ux.loading.getPricesForFullYear = false;
          if (status === Status.OK) {
            ux.warnings.seasonalRoute = isNotEmpty(payload.availability);
            price.availability = payload.availability;
            price.fullYear = payload.prices;
            price.histogramData = formatToHistogramData(payload.lowestPriceForMonth, payload.minPriceForYear);
            price.weeklyFlights = payload.weeklyFlights || [];
            price.error = payload.errorCode;
            if (price.weeklyFlights.length === 0) {
              selection.directFlights = false;
            }
          }
          // TODO: show error?
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const { envConfig, selection, ux } = readOnlyState;

      effectActions._getPricesForFullYearStart();
      getPricesForFullYear(envConfig, selection, ux.currentDate).then((response) => {
        effectActions._getPricesForFullYearFinish(response);
      });
    },
    when: (prev: BookingState, next: BookingState) => {
      return (
        (preDatesExcludingPaxAmountChanged(prev, next) || currentDateChanged(prev, next)) &&
        preDatesSelected(next.selection) &&
        currentDateAvailable(next) &&
        next.selection.travelType !== TravelType.MULTI_CITY
      );
    },
    name: getPricesForFullYearEffect.name,
  };
};

export const clearSeasonalRouteEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _clearSeasonalRouteWarning: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.warnings.seasonalRoute = false;
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._clearSeasonalRouteWarning,
    when: (prev: BookingState, next: BookingState) => locationsChanged(prev, next),
    name: clearSeasonalRouteEffect.name,
  };
};

export const getPricesForFollowingMonthEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getPricesForFollowingMonthStart: (state) => {
        const { origin, destination } = state.selection.locations[state.selection.activeLocationIndex];

        if (state.selection.travelDates.departureDate && origin && destination) {
          return produce(state, (state: BookingState) => {
            const { ux, price } = state;
            state.forceUpdate = false;
            ux.loading.getPricesForFollowingMonth = true;
            delete price.followingMonth;
          });
        }

        return produce(state, (state: BookingState) => {
          state.forceUpdate = false;
        });
      },
      _getPricesForFollowingMonthFinish: (state, { status, payload }: Response<PriceData>) => {
        const { origin, destination } = state.selection.locations[state.selection.activeLocationIndex];

        if (state.selection.travelDates.departureDate && origin && destination) {
          return produce(state, ({ ux, price }: BookingState) => {
            ux.loading.getPricesForFollowingMonth = false;
            if (status === Status.OK) {
              price.followingMonth = payload.prices;
            }
            // TODO: show error?
          });
        }

        return state;
      },
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const { envConfig, selection } = readOnlyState;

      effectActions._getPricesForFollowingMonthStart();
      getPricesForFollowingMonth(envConfig, selection).then((response) => {
        effectActions._getPricesForFollowingMonthFinish(response);
      });
    },
    when: (prev: BookingState, next: BookingState) =>
      next.forceUpdate ||
      (preDatesOrDepartureChanged(prev, next) &&
        preDatesAndDepartureSelected(next.selection) &&
        next.selection.travelType === TravelType.ROUND_TRIP),
    runOn: [
      {
        type: LifecycleEvent.INIT,
        when: (_prev: BookingState, next: BookingState) => {
          const { origin, destination } = next.selection.locations[next.selection.activeLocationIndex];

          return !!(next.selection.travelDates.departureDate && origin?.locationCode && destination?.locationCode);
        },
      },
    ],
    name: getPricesForFollowingMonthEffect.name,
  };
};
export const cancelGetPricesForFollowingMonthEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _cancelGetPricesForFollowingMonth: (state) => {
        cancelGetPricesForFollowingMonth();
        return state;
      },
    },
    store
  );

  return {
    runSideEffects: effectActions._cancelGetPricesForFollowingMonth,
    when: (prev: BookingState, next: BookingState) =>
      isLoading(next, Service.GET_PRICES_FOR_FOLLOWING_MONTH) &&
      preDatesOrDepartureChanged(prev, next) &&
      (!preDatesAndDepartureSelected(next.selection) || next.selection.travelType === TravelType.ONE_WAY),
    name: cancelGetPricesForFollowingMonthEffect.name,
  };
};

export const updatePriceStartingFromEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _updatePriceStartingFrom: (state) =>
        produce(state, ({ selection, price }: BookingState) => {
          if (preDatesAndDepartureSelected(selection)) {
            price.startingFrom = calculateCurrentLowestPrice(selection, price);
          } else {
            delete price.startingFrom;
          }
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._updatePriceStartingFrom,
    when: (prev: BookingState, next: BookingState) =>
      (changed(prev, next, 'selection', 'travelDates') ||
        changed(prev, next, 'price', 'fullYear') ||
        changed(prev, next, 'price', 'followingMonth')) &&
      next.selection.travelType !== TravelType.MULTI_CITY,
    name: updatePriceStartingFromEffect.name,
  };
};

export const getAirCalendarEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _getAirCalendarStart: (state) =>
        produce(state, ({ ux }: BookingState) => {
          ux.loading.getAirCalendar = true;
        }),
      _getAirCalendarFinish: (state, { status, payload }: Response<AirCalendarData>) =>
        produce(state, ({ ux, price, currentTab }: BookingState) => {
          ux.loading.getAirCalendar = false;
          ux.proceedToBooking = false;
          if (status === Status.OK) {
            price.airCalendar = payload.prices;
            ux.warnings.noFlights = isEmpty(payload.prices);
          } else if (status === Status.ERROR) {
            // Unknown error happened, directing to the booking flow
            const instantSearchPrice = state.price.startingFrom?.amount;
            state.extensionApi.navigator.navigateToBooking(
              bookingFlowDeepLink(state.envConfig, state.selection, currentTab === WidgetTab.AWARD),
              { instantSearchPrice }
            );
          }
        }),
    },
    store
  );

  return {
    runSideEffects: (readOnlyState) => {
      const {
        envConfig,
        selection,
        extensionApi: { getLoginToken },
      } = readOnlyState;

      effectActions._getAirCalendarStart();
      getAirCalendar(envConfig, selection, getLoginToken).then((response) => {
        effectActions._getAirCalendarFinish(response);
      });
    },
    when: (prev: BookingState, next: BookingState) => {
      const isAward = next.currentTab === WidgetTab.AWARD;
      const isFlexible = isAward || next.selection.travelDates.flexible;

      return changed(prev, next, 'ux', 'proceedToBooking') && next.ux.proceedToBooking && isFlexible;
    },

    name: getAirCalendarEffect.name,
  };
};

export const cancelAirCalendarEffect = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _cancelFlightAvailability: (state) => {
        cancelAirCalendar();
        return state;
      },
    },
    store
  );

  return {
    runSideEffects: effectActions._cancelFlightAvailability,
    when: (prev, next) => selectionChanged(prev, next) && next.ux.loading.getAirCalendar,
    name: cancelAirCalendarEffect.name,
  };
};

export const updateFlexibleDatesSelected = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _updateFlexibleDatesSelected: (state) =>
        produce(state, ({ selection, price }: BookingState) => {
          selection.travelDates.flexible = price.error === UNSUPPORTED_OND_STATUS_CODE;
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._updateFlexibleDatesSelected,
    when: (prev: BookingState, next: BookingState) => changed(prev, next, 'price', 'error'),
    name: updateFlexibleDatesSelected.name,
  };
};

export const clearPricesWhenMultiCity = (store: Store<BookingState>): SideEffect<BookingState> => {
  const effectActions = bindActions(
    {
      _clearPricesWhenMultiCity: (state) =>
        produce(state, (newState: BookingState) => {
          newState.price = {};
        }),
    },
    store
  );

  return {
    runSideEffects: effectActions._clearPricesWhenMultiCity,
    when: (prev: BookingState, next: BookingState) =>
      prev.selection.travelType !== TravelType.MULTI_CITY && next.selection.travelType === TravelType.MULTI_CITY,
    name: clearPricesWhenMultiCity.name,
  };
};

export const priceEffects: SideEffectGenerator<BookingState>[] = [
  getPricesForFullYearEffect,
  getPricesForFollowingMonthEffect,
  cancelGetPricesForFollowingMonthEffect,
  updatePriceStartingFromEffect,
  getAirCalendarEffect,
  cancelAirCalendarEffect,
  clearSeasonalRouteEffect,
  updateFlexibleDatesSelected,
  clearPricesWhenMultiCity,
];
